// サイドバー + ヘッダー

import React from "react";
import logo from "../assets/hsd-logo.png";
import { Ic } from "./Icons";

type NavItem = { key: string; label: string; icon: keyof typeof Ic; disabled?: boolean };
type NavGroup = { group: string; items: NavItem[] };

const NAV: NavGroup[] = [
  {
    group: "業務",
    items: [
      { key: "home", label: "ホーム", icon: "home" },
      { key: "csv", label: "CSV取込", icon: "upload" },
      { key: "list", label: "支払明細 一覧", icon: "list" },
      { key: "saved", label: "保存済みデータ", icon: "history" },
      { key: "approval", label: "承認状況", icon: "checkCircle" },
    ],
  },
  {
    group: "管理",
    items: [
      { key: "master", label: "マスター編集", icon: "users" },
      { key: "entry", label: "入社・契約解除", icon: "edit" },
      { key: "register", label: "メアド収集", icon: "send" },
      { key: "accounts", label: "アカウント管理", icon: "shield" },
      { key: "settings", label: "設定", icon: "settings" },
    ],
  },
];

export function Sidebar({
  current,
  onNav,
  open = false,
  onClose,
}: {
  current: string;
  onNav: (key: string) => void;
  /** 携帯幅で引き出しを開いているか（PC幅では常時表示なので無関係） */
  open?: boolean;
  onClose?: () => void;
}) {
  return (
    <>
      {/* 携帯で引き出しを開いている間だけ、後ろの画面を覆う。タップで閉じる */}
      <div
        className={"sidebar-scrim" + (open ? " open" : "")}
        onClick={onClose}
        aria-hidden="true"
      />
      <aside className={"sidebar" + (open ? " open" : "")}>
        <div className="sidebar-brand">
          <div className="brand-plate"><img src={logo} alt="HS-deliverys" /></div>
          <div className="col gap-4">
            <div className="brand-name">月次処理</div>
          </div>
          <button className="sidebar-close" onClick={onClose} aria-label="メニューを閉じる">
            <Ic.x size={18} />
          </button>
        </div>

        {NAV.map((g) => (
          <div key={g.group}>
            <div className="nav-group-label">{g.group}</div>
            {g.items.map((it) => {
              const Icon = Ic[it.icon];
              const active = current === it.key;
              return (
                <div
                  key={it.key}
                  className={"nav-item" + (active ? " active" : "") + (it.disabled ? " disabled" : "")}
                  onClick={() => {
                    if (it.disabled) return;
                    onNav(it.key);
                    onClose?.();
                  }}
                  style={it.disabled ? { opacity: 0.5, cursor: "not-allowed" } : undefined}
                >
                  <Icon size={16} className="nav-icon" />
                  <span>{it.label}</span>
                </div>
              );
            })}
          </div>
        ))}

        <div className="sidebar-foot">
          <div>橋本商会 → 株式会社ICB</div>
          <div style={{ marginTop: 2 }}>株式会社HS-deliverys 月次処理</div>
        </div>
      </aside>
    </>
  );
}

type Badge = { label: string; tone?: "amber" | "green"; title?: string };

export function Header({
  crumbs,
  period,
  badge,
  autosave,
  user,
  onLogout,
  onMenu,
}: {
  crumbs: string[];
  period?: string;
  badge?: Badge;
  autosave?: Badge;
  user?: { name: string; org?: string; role?: string } | null;
  onLogout?: () => void;
  /** 携帯幅でだけ出るメニューボタン */
  onMenu?: () => void;
}) {
  return (
    <header className="header">
      {onMenu && (
        <button className="header-menu-btn" onClick={onMenu} aria-label="メニューを開く">
          <Ic.menu size={20} />
        </button>
      )}
      <nav className="breadcrumb">
        {crumbs.map((c, i) => (
          <React.Fragment key={i}>
            {i > 0 && <Ic.chevronR size={12} className="sep" />}
            <span className={i === crumbs.length - 1 ? "current" : ""}>{c}</span>
          </React.Fragment>
        ))}
      </nav>
      <div className="header-spacer" />
      <div className="header-tools">
        {autosave && (
          <div
            className="period-pill pill-save"
            title={autosave.title ?? "取込内容と支払明細はブラウザに自動保存されます。タブを閉じても次回開いたときに復元されます。"}
            style={
              autosave.tone === "green"
                ? { color: "var(--green, #1d6b34)", background: "#e8f7ec", borderColor: "#a8dbb1" }
                : { color: "var(--amber, #8d5a16)", background: "var(--amber-soft, #fff4e0)", borderColor: "#f1d99a" }
            }
          >
            <Ic.checkCircle size={13} />
            <span>{autosave.label}</span>
          </div>
        )}
        {period && (
          <div className="period-pill pill-period">
            <Ic.clock size={13} />
            <span>処理対象 <b>{period}</b></span>
          </div>
        )}
        {badge && (
          <div
            className="period-pill pill-info"
            style={
              badge.tone === "amber"
                ? { color: "var(--amber)", background: "var(--amber-soft)", borderColor: "#f1d99a" }
                : badge.tone === "green"
                ? { color: "var(--green)", background: "var(--green-soft)", borderColor: "#bfe6c4" }
                : undefined
            }
          >
            <Ic.info size={13} />
            <span>{badge.label}</span>
          </div>
        )}
        {user ? (
          <>
            <div className="col gap-4" style={{ alignItems: "flex-end", lineHeight: 1.25, marginLeft: 4 }}>
              <div style={{ fontSize: 12, fontWeight: 700 }}>
                {user.name}
                {user.role === "admin" && (
                  <span className="chip" style={{ marginLeft: 5, padding: "1px 5px", fontSize: 10 }}>管理者</span>
                )}
              </div>
              {user.org && <div className="tiny muted">{user.org}</div>}
            </div>
            {onLogout && (
              <button className="btn sm ghost" onClick={onLogout} title="ログアウト">
                <Ic.logOut size={13} />
              </button>
            )}
          </>
        ) : (
          <div className="avatar">ICB</div>
        )}
      </div>
    </header>
  );
}
